<template>
  <div class="pb-5">
    <gat-form :readonly="!allowSave" v-model="isValid" ref="form" :isLoading="isLoading">
      <GatGroup
        class="pa-0"
        title="Customer / supplier details"
        :readonly="!$store.getters.userRight('C.EDIT')"
        :collapsed="isNew ? !expandedIfNew : !(value && value.ID == 0)">
        <template slot="compact">
          <v-layout wrap class="d-flex flex-row">
            <gat-compact-field :value="client.NAME" label="Company name" />
            <gat-compact-field
              :value="categoryName(client.CATEGORY)"
              label="Category"
              v-if="$store.state.user.internal" />
            <gat-compact-field :value="client.PHONE" label="Phone no">
              <a class="gridLink" :href="`tel:${client.PHONE}`" @click.stop>{{ client.PHONE }}</a>
            </gat-compact-field>
            <gat-compact-field :value="client.EMAIL" label="Email">
              <a class="gridLink" :href="`mailto:${client.EMAIL}`" @click.stop>{{ client.EMAIL }}</a>
            </gat-compact-field>
            <gat-compact-field :value="client.ACCOUNT_NO" label="Client no" size="xs" />
            <gat-compact-field
              v-if="client.INACTIVE"
              :value="client.INACTIVE"
              label="Inactive"
              :checkbox="true"
              size="xs" />
          </v-layout>
          <gat-dyna-fields
            v-if="$store.state.user.internal"
            v-model="client.dynaValues"
            componentName="tsOurData.ClientDyna"
            container="Upper part of window"
            :compact="true"></gat-dyna-fields>
        </template>

        <v-layout wrap>
          <gat-edit
            v-model="client.NAME"
            :autofocus="value && value.ID == 0"
            label="Name"
            size="xs12 s12 md4"
            :required="true"
            :maxChars="getColumnLength('Client.NAME')" />
          <gat-edit
            v-model="client.ALPHANAME"
            label="Alt name"
            v-if="$store.state.user.internal"
            size="xs12 md4"
            :maxChars="getColumnLength('Client.ALPHANAME')" />
          <gat-edit
            v-model="client.PHONE"
            label="Phone"
            size="xs12 md4"
            :readonly="!($store.getters.userRight('C.EDIT') || !$store.state.user.internal)"
            :maxChars="getColumnLength('Client.PHONE')"
            tel />
          <div class="d-flex ma-0 pa-0" :class="$vuetify.breakpoint.mdAndDown ? ' col-12 row-1' : ' col-4 row-1'">
            <gat-edit
              v-model="client.ACCOUNT_NO"
              label="Client no"
              v-if="$store.state.user.internal"
              :disabled="$store.state.globalSettings.SGL_ENABLE_CLIENT_CODE === 0"
              size="xs4"
              :required="isRequired('SGL_CLIENT_MAN_NUMBER')"
              :maxChars="getColumnLength('Client.ACCOUNT_NO')" />
            <gat-select
              v-model="client.CATEGORY"
              label="Category"
              v-if="$store.state.user.internal"
              :required="$store.state.globalSettings.SGL_CLIENT_MAN_CATEGORY === 1"
              size="xs8"
              :items="clientCategories"
              :clearable="$store.state.globalSettings.SGL_CLIENT_MAN_CATEGORY !== 1">
            </gat-select>
          </div>
          <gat-edit
            v-model="client.OFFICE_DESCRIPTION"
            label="Office description"
            size="xs12 md4"
            :maxChars="getColumnLength('Client.OFFICE_DESCRIPTION')" />
          <gat-edit
            v-model="client.EMAIL"
            label="Email"
            size="xs12 md4"
            :readonly="!($store.getters.userRight('C.EDIT') || !$store.state.user.internal)"
            :required="isRequired('SGL_CLIENT_MAN_EMAIL')"
            :maxChars="getColumnLength('Client.EMAIL')"
            mailto />
        </v-layout>
        <gat-dyna-fields
          v-if="$store.state.user.internal"
          v-model="client.dynaValues"
          componentName="tsOurData.ClientDyna"
          container="Upper part of window"
          :compact="false"></gat-dyna-fields>
        <v-layout wrap>
          <gat-edit
            v-model="client.INACTIVE"
            label="Inactive"
            size="s"
            checkbox
            :checkedValue="1"
            :uncheckedValue="0"
            :readonly="!$store.state.user.internal">
          </gat-edit>
          <gat-edit
            v-show="$store.state.globalSettings.SGL_USE_WEBMODULE_2021 === webModuleInternalAndExternal"
            v-model="client.WEBMODULEACCESS"
            label="External web module access"
            size="sm"
            checkbox
            :checkedValue="1"
            :uncheckedValue="0"
            :readonly="!$store.state.user.internal">
          </gat-edit>
        </v-layout>
      </GatGroup>

      <GatGroup
        v-if="$store.state.user.internal"
        class="pa-0"
        title="Remarks"
        :readonly="!$store.getters.userRight('C.EDIT')"
        :collapsed="isNew ? !expandedIfNew : value.ID ? true : false">
        <template slot="compact">
          <v-layout class="pr-3">
            <gat-compact-field v-model="client.NEW_COMMENTS" label="Comments" size="xs4" />
            <gat-compact-field v-model="client.NEW_INSTRUCTIONS" label="Instructions" size="xs4" />
          </v-layout>
        </template>
        <v-layout wrap>
          <GatSubGroup title="" size="xs12">
            <gat-edit
              v-model="client.NEW_COMMENTS"
              label="Comments"
              size="xs12 lg6"
              :rows="5"
              :maxChars="getColumnLength('Client.COMMENTS')">
            </gat-edit>
            <gat-edit v-model="client.NEW_INSTRUCTIONS" label="Instructions" size="xs12 lg6" :rows="5" :maxChars="0">
            </gat-edit>
          </GatSubGroup>
        </v-layout>
      </GatGroup>
      <GatGroup
        v-if="$store.state.user.internal"
        class="pa-0"
        title="Alert"
        :readonly="!$store.getters.userRight('C.EDIT')"
        :collapsed="isNew ? !expandedIfNew : value.ID ? true : false">
        <template slot="compact">
          <v-layout class="pr-3">
            <gat-compact-field
              v-model="client.WARNING"
              size="xs4"
              prepend-icon="notification_important"
              icon-color="orange" />
          </v-layout>
        </template>
        <v-layout wrap>
          <gat-edit
            v-model="client.SHOW_WARNING"
            label="Show alert when this client is used"
            size="xs12"
            checkbox
            :checkedValue="1"
            :uncheckedValue="0">
          </gat-edit>
          <gat-edit v-model="client.WARNING" label="Alert message" size="xs12" :rows="5" :maxChars="0"> </gat-edit>
        </v-layout>
      </GatGroup>

      <GatGroup
        class="pa-0"
        title="Addresses"
        :readonly="!($store.getters.userRight('C.EDIT') || !$store.state.user.internal)"
        :collapsed="isNew ? !expandedIfNew : !(value && value.ID == 0)">
        <template slot="compact">
          <v-layout wrap>
            <gat-compact-field
              :value="client.ADDRESS + ', ' + client.ZIP + ' ' + client.CITY + ', ' + client.NATIONAL_CODES_ID"
              label="Post"
              size="xs6" />
            <gat-compact-field
              v-if="client.ADR_MULTI"
              :value="
                client.ADR_INVOICE_STREET +
                ', ' +
                client.ADR_INVOICE_ZIP +
                ' ' +
                client.ADR_INVOICE_CITY +
                ', ' +
                client.ADR_INVOICE_COUNTRY_CODE
              "
              label="Invoice"
              size="xs6" />
          </v-layout>
        </template>
        <v-layout wrap>
          <GatSubGroup title="Post address">
            <gat-edit
              v-model="client.ADDRESS"
              label="Street address"
              size="xs12"
              :rows="2"
              :required="isRequired('SGL_CLIENT_MAN_POST_ADDRESS')">
            </gat-edit>
            <gat-edit
              v-model="client.ZIP"
              label="Zip"
              size="xs4"
              :required="isRequired('SGL_CLIENT_MAN_POST_ZIP')"
              :maxChars="getColumnLength('Client.ZIP')">
            </gat-edit>
            <gat-edit
              v-model="client.CITY"
              label="City"
              size="xs8"
              :required="isRequired('SGL_CLIENT_MAN_POST_CITY')"
              :maxChars="getColumnLength('Client.CITY')">
            </gat-edit>
            <gat-select
              v-model="client.NATIONAL_CODES_ID"
              label="Country"
              size="xs12"
              :items="countries"
              :required="isRequired('SGL_CLIENT_MAN_POST_COUNTRY')">
            </gat-select>
          </GatSubGroup>
          <GatSubGroup title="Visit address">
            <gat-edit v-model="client.ADR_VISIT_STREET" label="Street address" size="xs12" :rows="2" :maxChars="0">
            </gat-edit>
            <gat-edit
              v-model="client.ADR_VISIT_ZIP"
              label="Zip"
              size="xs4"
              :maxChars="getColumnLength('Client.ADR_VISIT_ZIP')">
            </gat-edit>
            <gat-edit
              v-model="client.ADR_VISIT_CITY"
              label="City"
              size="xs8"
              :maxChars="getColumnLength('Client.ADR_VISIT_CITY')">
            </gat-edit>
            <gat-select v-model="client.ADR_VISIT_COUNTRY_CODE" label="Country" size="xs12" :items="countries">
            </gat-select>
          </GatSubGroup>
          <GatSubGroup title="Invoice address">
            <gat-edit v-model="client.ADR_INVOICE_STREET" label="Street address" size="xs12" :rows="2"> </gat-edit>
            <gat-edit
              v-model="client.ADR_INVOICE_ZIP"
              label="Zip"
              size="xs4"
              :maxChars="getColumnLength('Client.ADR_INVOICE_ZIP')">
            </gat-edit>
            <gat-edit
              v-model="client.ADR_INVOICE_CITY"
              label="City"
              size="xs8"
              :maxChars="getColumnLength('Client.ADR_INVOICE_CITY')">
            </gat-edit>
            <gat-select v-model="client.ADR_INVOICE_COUNTRY_CODE" label="Country" size="xs12" :items="countries">
            </gat-select>
          </GatSubGroup>
        </v-layout>
      </GatGroup>

      <GatGroup
        class="pa-0"
        title="Financial"
        :readonly="!$store.getters.userRight('C.EDIT')"
        :collapsed="isNew ? !expandedIfNew : !(value && value.ID == 0)">
        <template slot="compact">
          <v-layout wrap>
            <gat-compact-field :value="client.CURRENCY_NAME" label="Currency" size="xs" />
            <gat-compact-field
              v-if="client.CREDIT_TERM"
              :value="client.CREDIT_TERM + ' days'"
              label="Invoice terms"
              size="xs" />
            <gat-compact-field :value="client.ADVANCE" label="Ask for advance" checkbox size="xs" />
            <gat-compact-field
              v-if="client.HANDLING_PERCENT"
              :value="client.HANDLING_PERCENT + ' %'"
              label="Handling"
              size="xs" />
            <gat-compact-field :value="client.CREDIT_LIMIT" label="Credit limit" size="xs" />
            <gat-compact-field :value="client.FIXED_FEE" label="Agency fee" size="xs" />
            <gat-compact-field
              v-if="client.REBATE_PERCENT"
              :value="client.REBATE_PERCENT + ' %'"
              label="AF Rebate"
              size="xs" />
            <gat-compact-field :value="client.REBATE_FIXED" label="AF fixed rebate" size="xs" />
          </v-layout>
        </template>

        <v-layout wrap>
          <GatSubGroup title="Bank details">
            <gat-edit
              v-model="client.BANK_DETAILS"
              label="Bank name and address"
              size="xs12"
              :rows="2"
              :maxChars="getColumnLength('Client.BANK_DETAILS')"
              :readonly="!$store.state.user.internal" />
            <gat-edit
              v-model="client.BANK_SWIFT"
              label="BIC / Swift"
              size="xs12"
              :maxChars="getColumnLength('Client.BANK_SWIFT')"
              :readonly="!$store.state.user.internal" />
            <gat-edit
              v-model="client.BANK_ACCOUNT"
              label="Account no"
              size="xs12"
              :maxChars="getColumnLength('Client.BANK_ACCOUNT')"
              :readonly="!$store.state.user.internal" />
            <gat-edit
              v-model="client.BANK_IBAN"
              label="IBAN"
              size="xs12"
              :maxChars="getColumnLength('Client.BANK_IBAN')"
              :readonly="!$store.state.user.internal" />
          </GatSubGroup>

          <GatSubGroup title="Organisation">
            <gat-edit
              v-model="client.VAT_NO"
              label="VAT no"
              size="xs12"
              :readonly="
                !(
                  this.$store.getters.userRight('C.EDIT_VAT_NUMBER') ||
                  this.$store.getters.userRight('OC.EDIT_VAT_NUMBER')
                )
              "
              :required="isRequired('SGL_CLIENT_MAN_VAT', 'SGL_VAT_MANDATORY')"
              :maxChars="getColumnLength('Client.VAT_NO')" />
            <gat-edit
              v-model="client.EORI_NO"
              label="EORI no"
              size="xs12"
              :maxChars="getColumnLength('Client.EORI_NO')"
              :readonly="!$store.state.user.internal" />
            <gat-select
              v-model="client.CURRENCY_NAME"
              label="Currency"
              size="xs6"
              :items="currencies"
              :readonly="!$store.state.user.internal" />
          </GatSubGroup>

          <GatSubGroup title="Expense settings" v-if="$store.state.user.internal">
            <gat-edit v-model="client.HANDLING_PERCENT" label="Handling" suffix="%" size="xs6" dataType="number">
            </gat-edit>
            <!--  -->
            <gat-edit v-model="client.TENDER_LIMIT" label="Tender limit" size="xs6" dataType="number"> </gat-edit>
            <gat-edit v-model="client.CREDIT_LIMIT" label="Credit limit" size="xs6" dataType="number"> </gat-edit>
            <gat-edit v-model="client.CREDIT_TERM" label="Invoice term" size="xs6" suffix="days" dataType="number">
            </gat-edit>

            <gat-edit
              v-model="client.ADVANCE"
              label="Ask for advance"
              size="xs12"
              checkbox
              :checkedValue="1"
              :uncheckedValue="0">
            </gat-edit>
          </GatSubGroup>

          <GatSubGroup title="Agency fee settings" v-if="$store.state.user.internal">
            <gat-edit v-model="client.FIXED_FEE" dataType="number" label="Fixed fee" size="xs6"> </gat-edit>
            <gat-edit v-model="client.REBATE_PERCENT" dataType="number" label="Rebate" suffix="%" size="xs6">
            </gat-edit>
            <gat-edit v-model="client.REBATE_FIXED" dataType="number" label="Reabte (fixed)" size="xs6"> </gat-edit>
          </GatSubGroup>
        </v-layout>
      </GatGroup>

      <GatGroup
        v-if="$store.state.user.internal"
        title="Our data"
        class="pa-0"
        :readonly="!$store.getters.userRight('C.EDIT')"
        :collapsed="isNew ? !expandedIfNew : !(value && value.ID == 0)">
        <template slot="compact">
          <gat-dyna-fields
            v-model="client.dynaValues"
            componentName="tsOurData.ClientDyna"
            container="Tab page"
            :compact="true"></gat-dyna-fields>
        </template>
        <gat-dyna-fields
          v-model="client.dynaValues"
          componentName="tsOurData.ClientDyna"
          container="Tab page"></gat-dyna-fields>
      </GatGroup>

      <GatGroup
        v-if="$store.state.user.internal"
        title="Type / role"
        class="pa-0"
        :collapsed="isNew ? !expandedIfNew : true">
        <template slot="compact">
          <v-layout wrap>
            <gat-flex v-if="client.roles && client.roles.length > 0" size="xs12">{{ getRoleDescription() }}</gat-flex>
          </v-layout>
        </template>
        <div v-if="client.ID">
          <v-layout wrap>
            <gat-grid
              :items="client.roles"
              :columns="roleColumns"
              class="elevation-0"
              @row-clicked="roleRowClicked"
              rowHint="click to edit">
            </gat-grid>
          </v-layout>
        </div>
        <v-btn
          v-if="client.ID"
          slot="btn"
          class="addxConsent"
          outlined
          color="primary"
          small
          @click="addRole"
          :disabled="!$store.getters.userRight('C.EDIT')">
          <v-icon small>mdi-plus</v-icon>Add
        </v-btn>
        <div v-else class="pa-3">The new contact have to be saved before this section can be edited</div>
      </GatGroup>

      <gat-bottom-panel
        v-if="true"
        :orgValue="orgClient"
        :newValue="client"
        :itemDescription="'client ' + client.NAME"
        :saveBtn="true"
        :deleteBtn="true"
        :buttonsEnabled="!saving"
        :deleteDisabled="!client.ID || !$store.getters.userRight('C.DELETE')"
        :saveDisabled="!allowSave"
        @saveClicked="saveClicked"
        @deleteClicked="deleteClicked"
        @leftWithoutSaving="leftWithoutSaving"></gat-bottom-panel>

      <client-role-form
        :showForm="showRoleForm"
        v-model="currentRole"
        @close="roleFormClosed"
        :readonly="!$store.getters.userRight('C.EDIT')"></client-role-form>
    </gat-form>
    <br />
  </div>
</template>

<script>
import GatUtils from '@/app-components/GatUtils';
import GatDynaFields from '@/app-components/dynafields/GatDynaFields.vue';
// eslint-disable-next-line import/no-cycle
import { apiPut, apiDelete } from '@/store/api';
import ClientRoleForm from './ClientRoleForm.vue';

export default {
  name: 'ClientMain',
  components: { GatDynaFields, ClientRoleForm },
  props: {
    value: [Object, Array],
    isNew: Boolean,
  },
  data() {
    return {
      expandedIfNew: false, // is set in activated/deactivated based on isNew prop.
      client: {},
      currentRole: {},
      orgClient: {},
      saving: false,
      showRoleForm: false,
      isValid: null,
      roleColumns: [],
      isLoading: false,
      webModuleInternalAndExternal: 2,
    };
  },
  activated() {
    this.expandedIfNew = this.isNew;
  },
  deactivated() {
    this.expandedIfNew = !this.isNew;
  },

  mounted() {
    // this.client = GatUtils.makeCopyViaJson(this.value);
    // this.orgClient =  GatUtils.makeCopyViaJson(this.client);
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        const value = { ...newValue };
        if (!value.dynaValues) value.dynaValues = [];
        this.client = GatUtils.makeCopyViaJson(value);
        this.orgClient = GatUtils.makeCopyViaJson(this.client);
      },
    },
  },

  computed: {
    allowSave() {
      if (this.$store.state.user.internal) {
        if (this.value && this.value.ID == 0) {
          return this.$store.getters.userRight('C.NEW');
        }
        return this.$store.getters.userRight('C.EDIT');
      }
      if (this.value && this.value.ID === this.$store.state.user.externalClientId) {
        return this.$store.getters.userRight('OC.EDIT');
      }

      return false;
    },
    countries() {
      return this.$store.state.helper.countries;
    },
    currencies() {
      const result = [];
      const cur = [...this.$store.state.helper.currencies];
      cur.forEach((item) => {
        const curItem = {};
        curItem.value = item.value;
        curItem.text = item.text;
        curItem.ROE = item.ROE;
        // eslint-disable-next-line no-restricted-syntax
        for (const i in item.IDS) {
          if (!curItem.ID) {
            curItem.ID = item.IDS[i];
          }
        }
        result.push(curItem);
      });
      return result;
    },
    clientTypes() {
      return this.$store.state.helper.clientTypes;
    },
    clientCategories() {
      return this.$store.state.helper.clientCategories;
    },
  },

  created() {
    const self = this;
    this.roleColumns = [
      {
        header: 'Type / role',
        calculated: (rowData) => this.lookupCode(rowData.CLIENT_TYPE_ID, self.clientTypes, 'ID', 'NAME'),
      },
      { header: 'Harbour', field: 'HarbourName', hideOnPhone: true },
      {
        header: 'Ranking',
        hideOnPhone: true,
        calculated: (rowData) =>
          this.lookupCode(rowData.CT_RANKING, this.$store.state.constantsclientTypeRanking, 'value', 'text'),
      },
      { header: 'Date', field: 'CT_RANKING_DATE', hideOnPhone: true, dataType: 'date' },
      { header: 'Remarks', field: 'CT_COMMENT', hideOnPhone: true },
    ];
  },

  methods: {
    isRequired(globalSetting, cat0) {
      /* This method takes in 2 arguments. globalSetting should be a string with the name of the global setting to be checked. cat0 is optional if the first setting name is different from the others. */
      // eslint-disable-next-line default-case
      switch (this.client.CATEGORY) {
        case 0: // customer / debitor
          if (this.$store.state.globalSettings[cat0]) {
            return true;
          }
          if (this.$store.state.globalSettings[globalSetting]) {
            return true;
          }
          break;
        case 1: // supplier / creditor
          // eslint-disable-next-line no-param-reassign
          globalSetting += '_CAT1';
          if (this.$store.state.globalSettings[globalSetting]) {
            return true;
          }
          break;
        case 2: // other
          // eslint-disable-next-line no-param-reassign
          globalSetting += '_CAT2';
          if (this.$store.state.globalSettings[globalSetting]) {
            return true;
          }
      }
      return false;
    },

    addRole() {
      this.currentRole = { CLIENT_ID: this.client.ID };
      this.showRoleForm = true;
    },

    categoryName(catId) {
      if ((catId || 0) >= 0) {
        return this.clientCategories[catId || 0].text;
      }
      return null;
    },

    getRoleDescription() {
      let result = this.lookupCode(this.client.roles[0].CLIENT_TYPE_ID, this.clientTypes, 'ID', 'NAME');
      for (let index = 1; index < this.client.roles.length; index++) {
        const role = this.client.roles[index];
        result = `${result}, ${this.lookupCode(role.CLIENT_TYPE_ID, this.clientTypes, 'ID', 'NAME')}`;
      }

      return result;
    },

    roleFormClosed(params) {
      this.showRoleForm = false;
      if (params.btn == 'save') {
        // set isLoading to true to show progressbar if response is not immidiate
        this.isLoading = true;
        apiPut('/clientRole', { value: params.value, orgValue: this.currentRole }).then((result) => {
          // remove progressbar
          this.isLoading = false;
          if (result.resultCategory == 1) {
            this.client.roles = result.payload;
            this.orgClient.roles = this.client.roles; // to avoid save btn "changes"
          }
        });
      } else if (params.btn == 'delete') {
        // set isLoading to true to show progressbar if response is not immidiate
        this.isLoading = true;
        apiDelete(`/clientRole/${params.value.ID}`).then(() => {
          // remove progressbar
          this.isLoading = false;
          const idx = this.client.roles.findIndex((item) => item.ID == params.value.ID);
          this.currentRole = params.value;
          this.client.roles.splice(idx, 1);
          this.orgClient.roles = this.client.roles; // to avoid save btn "changes"
        });
      }
      this.currentRole = {};
    },

    isMultiAddress() {
      const isMultiAddress =
        this.client.ADR_VISIT_STREET ||
        this.client.ADR_VISIT_ZIP ||
        this.client.ADR_VISIT_CITY ||
        this.client.ADR_VISIT_COUNTRY_CODE ||
        this.client.ADR_INVOICE_STREET ||
        this.client.ADR_INVOICE_ZIP ||
        this.client.ADR_INVOICE_CITY ||
        this.client.ADR_INVOICE_COUNTRY_CODE;
      if (isMultiAddress) {
        return 1;
      }
      return 0;
    },

    saveClicked() {
      this.$refs.form.validate();
      if (!this.isValid) return;

      if (!this.client.INACTIVE) this.client.INACTIVE = 0;

      this.client.ADR_MULTI = this.isMultiAddress();

      this.saving = true;
      // set isLoading to true to show progressbar if response is not immidiate
      this.isLoading = true;
      apiPut('/clients', {
        value: this.client,
        orgValue: this.orgClient,
        dynavalues: this.client.dynaValues,
        orgDynavalues: this.orgClient.dynaValues,
      }).then((result) => {
        this.saving = false;
        // remove progressbar
        this.isLoading = false;
        if (this.client.ID) {
          window.messageBus.$emit('clientUpdate', this.client);
        } else {
          window.messageBus.$emit('clientInsert', result.payload);
        }
        this.client = result.payload;
        this.orgClient = GatUtils.makeCopyViaJson(this.client);

        this.$nextTick(() => {
          if (this.value.ID == 0) {
            this.$router.replace(`/client/${result.payload.ID}`);
          }
        });
      });
    },

    deleteClicked() {
      this.saving = true;
      // set isLoading to true to show progressbar if response is not immidiate
      this.isLoading = true;
      apiDelete(`/clients/${this.client.ID}`).then((result) => {
        this.saving = false;
        // remove progressbar
        this.isLoading = false;
        window.messageBus.$emit('clientDelete', this.client.ID);
        this.$store.commit('removeFromLastClientUsed', this.client.ID);
        if (result.resultCategory == 1) {
          // this.$router.go(-1); //go back
          this.$router.push('/clients');
        }
      });
    },

    roleRowClicked(rowData) {
      this.currentRole = rowData;
      this.showRoleForm = true;
    },

    leftWithoutSaving() {
      this.client = GatUtils.makeCopyViaJson(this.value);
      this.orgClient = GatUtils.makeCopyViaJson(this.value);
    },
  },
};
</script>

<style scoped>
.nowrap {
  white-space: nowrap;
  overflow: hidden;
  -o-text-overflow: ellipsis; /* Opera < 11*/
  text-overflow: ellipsis;
}
</style>
